import { useEffect, useState } from 'react';
import { enviar } from '../api/cliente';
import { Aviso, Boton, Cargando, ErrorEnPantalla, Insignia, Panel } from '../comun/componentes';
import { useAccion, useDatos } from '../comun/useDatos';
import { legible } from '../comun/formato';
import { useSesion } from '../sesion/Sesion';

type AntecedenteCatalogo = {
  codigo: string;
  nombre: string;
  categoria: string;
  severidadPorDefecto: string;
  generaAlerta: boolean;
};

type AntecedenteEntrada = {
  codigo: string;
  severidad: string | null;
  detalle: string | null;
  vigenteDesde: string | null;
  vigenteHasta: string | null;
};

const SEVERIDADES = ['Baja', 'Media', 'Alta', 'Critica'];

/**
 * Anamnesis. Es una lista de casillas agrupada por categoría, no un formulario largo: el
 * profesional marca lo que el paciente refiere y agrega el detalle sólo donde hace falta.
 * Guardar crea una versión nueva; la anterior queda intacta, como toda la historia clínica.
 */
export function AnamnesisDelPaciente({
  pacienteId,
  alGuardar,
}: {
  pacienteId: string;
  alGuardar: () => void;
}) {
  const { puede } = useSesion();
  const editable = puede('anamnesis.escribir');

  const catalogo = useDatos<AntecedenteCatalogo[]>('/api/v1/catalogos/antecedentes');
  const vigente = useDatos<AntecedenteEntrada[]>(`/api/v1/pacientes/${pacienteId}/anamnesis`);
  const { correr, ejecutando, error } = useAccion();

  const [seleccion, setSeleccion] = useState<Map<string, AntecedenteEntrada>>(new Map());
  const [observaciones, setObservaciones] = useState('');
  const [guardado, setGuardado] = useState(false);

  useEffect(() => {
    if (vigente.datos) {
      setSeleccion(new Map(vigente.datos.map((a) => [a.codigo, a])));
    }
  }, [vigente.datos]);

  if (catalogo.cargando || vigente.cargando) {
    return <Cargando que="la anamnesis" />;
  }

  if (catalogo.error) {
    return <ErrorEnPantalla mensaje={catalogo.error} reintentar={catalogo.recargar} />;
  }

  const porCategoria = new Map<string, AntecedenteCatalogo[]>();
  for (const antecedente of catalogo.datos ?? []) {
    const lista = porCategoria.get(antecedente.categoria) ?? [];
    lista.push(antecedente);
    porCategoria.set(antecedente.categoria, lista);
  }

  const alternar = (antecedente: AntecedenteCatalogo) => {
    const copia = new Map(seleccion);

    if (copia.has(antecedente.codigo)) {
      copia.delete(antecedente.codigo);
    } else {
      copia.set(antecedente.codigo, {
        codigo: antecedente.codigo,
        severidad: antecedente.severidadPorDefecto,
        detalle: null,
        vigenteDesde: null,
        vigenteHasta: null,
      });
    }

    setSeleccion(copia);
  };

  const cambiar = (codigo: string, campo: 'severidad' | 'detalle', valor: string) => {
    const copia = new Map(seleccion);
    const actual = copia.get(codigo);

    if (actual) {
      copia.set(codigo, { ...actual, [campo]: valor || null });
      setSeleccion(copia);
    }
  };

  const guardar = async () => {
    const resultado = await correr(() =>
      enviar<{ version: number }>(`/api/v1/pacientes/${pacienteId}/anamnesis`, {
        pacienteId,
        antecedentes: [...seleccion.values()],
        observaciones: observaciones.trim() || null,
      }),
    );

    if (resultado) {
      setGuardado(true);
      vigente.recargar();
      alGuardar();
    }
  };

  return (
    <div className="space-y-4">
      {guardado && (
        <Aviso tono="exito" alCerrar={() => setGuardado(false)}>
          Anamnesis guardada. Las alertas de la ficha ya están actualizadas.
        </Aviso>
      )}

      {[...porCategoria.entries()].map(([categoria, antecedentes]) => (
        <Panel key={categoria} titulo={legible(categoria)}>
          <ul className="grid gap-3 tablet:grid-cols-2">
            {antecedentes.map((antecedente) => {
              const marcado = seleccion.get(antecedente.codigo);

              return (
                <li key={antecedente.codigo} className="rounded-lg border border-papel-200 p-3">
                  <label className="flex min-h-tactil cursor-pointer items-center gap-3">
                    <input
                      type="checkbox"
                      checked={marcado != null}
                      disabled={!editable}
                      onChange={() => alternar(antecedente)}
                      className="h-5 w-5 rounded border-papel-300 text-marca-600 focus:ring-marca-600"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-tinta-800">
                        {antecedente.nombre}
                      </span>
                      {antecedente.generaAlerta && (
                        <Insignia tono="aviso" className="mt-0.5">
                          genera alerta
                        </Insignia>
                      )}
                    </span>
                  </label>

                  {marcado && (
                    <div className="mt-3 grid gap-2 border-t border-papel-200 pt-3 tablet:grid-cols-[8rem_1fr]">
                      <label className="text-dato">
                        <span className="etiqueta-campo">Severidad</span>
                        <select
                          value={marcado.severidad ?? antecedente.severidadPorDefecto}
                          disabled={!editable}
                          onChange={(e) => cambiar(antecedente.codigo, 'severidad', e.target.value)}
                          className="campo mt-1"
                        >
                          {SEVERIDADES.map((severidad) => (
                            <option key={severidad} value={severidad}>
                              {severidad}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="text-dato">
                        <span className="etiqueta-campo">Detalle</span>
                        <input
                          type="text"
                          value={marcado.detalle ?? ''}
                          disabled={!editable}
                          placeholder="Medicación, fecha, aclaración…"
                          onChange={(e) => cambiar(antecedente.codigo, 'detalle', e.target.value)}
                          className="campo mt-1"
                        />
                      </label>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </Panel>
      ))}

      {editable && (
        <Panel titulo="Observaciones">
          <textarea
            rows={3}
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            placeholder="Lo que el paciente refiere y no entra en una casilla."
            className="campo"
          />

          {error && (
            <Aviso tono="alerta" className="mt-3">
              {error}
            </Aviso>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Boton variante="primario" cargando={ejecutando} onClick={() => void guardar()}>
              Guardar anamnesis
            </Boton>
            <span className="text-dato text-tinta-500">
              Se guarda como versión nueva; la anterior no se toca.
            </span>
          </div>
        </Panel>
      )}
    </div>
  );
}
